import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { Dumbbell, TrendingUp } from 'lucide-react';
import { supabase } from '../../../supabase';
import { colors, typography, radius, spacing } from '../../../theme';
import { weekKey } from '../../../utils/weekStreak';

const RANGES = [
  { value: '4', label: '4W' },
  { value: '12', label: '12W' },
  { value: 'all', label: 'All' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Calendar weeks for the chosen range, oldest first, ending with this week (Codex's review of the
 * client's Stats, task 017, M4). The database returns only weeks with training, so "4W" used to mean the
 * last four active weeks, which could span months. Weeks without training are zero; this week is marked
 * as in progress. range: '4', '12' or 'all' (from the first week with training).
 */
export const calendarWeeks = (rows, range, now = new Date()) => {
  const byWeek = new Map((rows || []).map(r => [r.week, r]));
  const thisWeek = weekKey(now);
  const first = [...byWeek.keys()].sort()[0];
  const count = range === 'all'
    ? (first ? Math.round((new Date(`${thisWeek}T12:00:00`) - new Date(`${first}T12:00:00`)) / (7 * DAY_MS)) + 1 : 0)
    : parseInt(range, 10);
  const weeks = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const monday = new Date(`${thisWeek}T12:00:00`);
    monday.setDate(monday.getDate() - 7 * i);
    const key = weekKey(monday);
    const row = byWeek.get(key);
    weeks.push({
      week: key,
      volumeKg: row ? row.volumeKg : 0,
      avgWeight: row && row.avgWeight > 0 ? row.avgWeight : null,
      inProgress: key === thisWeek,
    });
  }
  return weeks;
};

/**
 * The headline: the last full week against the average of the four full weeks before it, weeks without
 * training counted as zero. `weeks` is the whole calendar history from the first week with training
 * (calendarWeeks(rows, 'all')). With fewer than four full weeks before the last one there is no
 * comparison (tooNew): averaging the one or two weeks there are, while saying "the 4 weeks before",
 * misled (Codex's review of the Stats fixes, task 018, R2).
 */
export const lastFullWeek = (weeks) => {
  const full = weeks.filter(w => !w.inProgress);
  if (full.length === 0) return null;
  const latest = full[full.length - 1];
  if (full.length < 5) return { latest, volumeDeltaPct: null, weightDeltaPct: null, tooNew: true };
  const before = full.slice(full.length - 5, full.length - 1);
  const average = (key) => {
    const values = before.map(w => w[key]).filter(v => v != null);
    return values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
  };
  const change = (now, then) => (then > 0 && now != null ? Math.round(((now - then) / then) * 100) : null);
  return {
    latest,
    volumeDeltaPct: change(latest.volumeKg, average('volumeKg')),
    weightDeltaPct: change(latest.avgWeight, average('avgWeight')),
  };
};

const formatKg = (v) => {
  if (v < 1000) return `${v}`;
  const k = v / 1000;
  return `${Number.isInteger(k) ? k : k.toFixed(1)}k`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const volume = payload.find(p => p.dataKey === 'volumeKg')?.value;
  const avgWeight = payload.find(p => p.dataKey === 'avgWeight')?.value;
  const soFar = payload[0]?.payload?.inProgress;
  return (
    <div style={{
      background: colors.surface2, border: `1px solid ${colors.border}`, borderRadius: radius.sm,
      padding: '12px 14px', minWidth: '150px',
    }}>
      <div style={{ ...typography.caption, color: colors.text, fontWeight: '700', marginBottom: spacing.sm }}>
        Week of {new Date(`${label}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{soFar ? ' (so far)' : ''}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.lg, marginBottom: '4px' }}>
        <span style={{ ...typography.caption, color: colors.textMuted }}>Volume</span>
        <span style={{ ...typography.caption, color: colors.text, fontWeight: '600' }}>{volume?.toLocaleString()} kg</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.lg }}>
        <span style={{ ...typography.caption, color: colors.textMuted }}>Avg Weight</span>
        <span style={{ ...typography.caption, color: colors.text, fontWeight: '600' }}>{avgWeight != null ? `${avgWeight} kg` : '—'}</span>
      </div>
    </div>
  );
};

/**
 * Training Load — merges the old Weekly Volume + Volume Progression cards.
 * Weekly bucketing + bodyweight/assisted math happen in Postgres
 * (get_weekly_training_load, migration 055); this fetches the user's full
 * weekly-grouped history once (~1 row per active week, so no row-cap risk)
 * then windows it client-side per the 4W/12W/All control — no re-fetch on
 * range change.
 */
const TrainingLoad = ({ userId }) => {
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('12');

  useEffect(() => {
    if (userId) fetchWeeklyVolume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchWeeklyVolume = async () => {
    try {
      setLoading(true);
      // p_weeks: null => all weeks. The RPC returns one row per active week
      // already sorted ascending, so the 10k-row cap that used to freeze this
      // chart for heavy accounts no longer applies.
      const { data: rows, error: rpcError } = await supabase.rpc('get_weekly_training_load', {
        p_user_id: userId,
        p_weeks: null,
      });

      if (rpcError) throw rpcError;

      const data = (rows ?? []).map(r => ({
        week: r.week_start, // 'YYYY-MM-DD', Monday of the ISO week
        volumeKg: Math.round(Number(r.volume_kg) || 0),
        avgWeight: Math.round(Number(r.avg_weight) || 0),
      }));

      setWeeklyData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching training load:', error);
      setLoading(false);
    }
  };

  const windowed = useMemo(() => calendarWeeks(weeklyData, range), [weeklyData, range]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ ...typography.sectionHeading, color: colors.text }}>Training Load</div>
        <div style={{ textAlign: 'center', padding: spacing['3xl'], color: colors.textMuted }}>Loading...</div>
      </div>
    );
  }

  if (weeklyData.length === 0) {
    return (
      <div style={styles.container}>
        <div style={{ ...typography.sectionHeading, color: colors.text, marginBottom: spacing.md }}>Training Load</div>
        <div style={{ textAlign: 'center', padding: spacing['2xl'], color: colors.textMuted }}>
          Complete workouts to see your training load here.
        </div>
      </div>
    );
  }

  // The headline is the last full week, dated; this week is shown as "so far" and never compared. Its
  // comparison uses all weeks in the full calendar history, not only the chosen range
  const headline = lastFullWeek(calendarWeeks(weeklyData, 'all'));
  const latest = headline?.latest ?? { volumeKg: 0, avgWeight: null, week: null };
  const volumeDeltaPct = headline?.volumeDeltaPct ?? null;
  const weightDeltaPct = headline?.weightDeltaPct ?? null;
  const thisWeek = windowed[windowed.length - 1];
  const fullWeeks = windowed.filter(w => !w.inProgress);
  const avgWeeklyVolume = fullWeeks.length ? Math.round(fullWeeks.reduce((s, w) => s + w.volumeKg, 0) / fullWeeks.length) : 0;
  const peakWeek = Math.max(0, ...windowed.map(w => w.volumeKg));
  const latestLabel = latest.week
    ? `Week of ${new Date(`${latest.week}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : 'No full week yet';
  // Up is green; down is plain text, not red: a lighter week is often the plan
  const deltaColor = (pct) => (pct > 0 ? colors.success : colors.text);

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
        <div style={{ ...typography.sectionHeading, color: colors.text }}>Training Load</div>
        <div style={{ display: 'flex', background: colors.surface2, borderRadius: radius.pill, padding: '3px' }}>
          {RANGES.map(r => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              style={{
                padding: '5px 12px',
                minHeight: '44px',
                borderRadius: radius.pill,
                border: 'none',
                cursor: 'pointer',
                ...typography.caption,
                fontWeight: '700',
                background: range === r.value ? colors.gold : 'transparent',
                color: range === r.value ? colors.bg : colors.textMuted,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.lg, flexWrap: 'wrap' }}>
        <div style={styles.kpiBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
            <Dumbbell size={13} strokeWidth={2.3} color={colors.gold} />
            <span style={{ ...typography.label, color: colors.textMuted }}>Last full week</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing.sm }}>
            <span style={{ color: colors.gold, fontSize: '18px', fontWeight: '700' }}>{formatKg(latest.volumeKg)} kg</span>
            {volumeDeltaPct != null && (
              <span style={{ ...typography.caption, fontWeight: '700', color: deltaColor(volumeDeltaPct) }}>
                {volumeDeltaPct >= 0 ? '+' : ''}{volumeDeltaPct}%
              </span>
            )}
          </div>
          <div style={{ ...typography.caption, color: colors.textMuted, marginTop: '4px' }}>
            {latestLabel}{volumeDeltaPct != null ? ' · vs. the 4 weeks before' : ''}
            {headline?.tooNew ? ' · a comparison starts after 5 full weeks of training' : ''}
          </div>
        </div>
        <div style={styles.kpiBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
            <TrendingUp size={13} strokeWidth={2.3} color={colors.success} />
            <span style={{ ...typography.label, color: colors.textMuted }}>Avg lifted weight</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing.sm }}>
            <span style={{ color: colors.success, fontSize: '18px', fontWeight: '700' }}>{latest.avgWeight != null ? `${latest.avgWeight} kg` : '—'}</span>
            {weightDeltaPct != null && (
              <span style={{ ...typography.caption, fontWeight: '700', color: deltaColor(weightDeltaPct) }}>
                {weightDeltaPct >= 0 ? '+' : ''}{weightDeltaPct}%
              </span>
            )}
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={windowed} margin={{ top: 10, right: 4, left: 0, bottom: 6 }} barCategoryGap="28%">
          <defs>
            <linearGradient id="gradTrainingLoad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.gold} stopOpacity={0.9} />
              <stop offset="100%" stopColor={colors.gold} stopOpacity={0.6} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
          <XAxis
            dataKey="week"
            stroke={colors.textMuted}
            tick={{ fill: colors.textMuted, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value) => new Date(`${value}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          />
          <YAxis yAxisId="left" stroke={colors.gold} tick={{ fill: colors.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} width={40} tickFormatter={formatKg} />
          <YAxis yAxisId="right" orientation="right" stroke={colors.success} tick={{ fill: colors.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} width={34} />
          <Tooltip content={<CustomTooltip />} />
          <Bar yAxisId="left" dataKey="volumeKg" fill="url(#gradTrainingLoad)" radius={[6, 6, 0, 0]} maxBarSize={30} />
          <Line yAxisId="right" type="monotone" dataKey="avgWeight" stroke={colors.success} strokeWidth={2.5} dot={{ fill: colors.success, r: 4, stroke: colors.surface1, strokeWidth: 2 }} activeDot={{ r: 6 }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>

      {thisWeek?.inProgress && (
        <div style={{ ...typography.caption, color: colors.textMuted, marginTop: spacing.sm }}>
          This week so far: {thisWeek.volumeKg.toLocaleString()} kg (the last bar)
        </div>
      )}

      <div style={styles.summaryRow}>
        <div>
          <div style={{ ...typography.label, color: colors.textMuted }}>Avg full week</div>
          <div style={{ ...typography.body, color: colors.text, fontWeight: '600', marginTop: '2px' }}>{avgWeeklyVolume.toLocaleString()} kg</div>
        </div>
        <div>
          <div style={{ ...typography.label, color: colors.textMuted }}>Peak week</div>
          <div style={{ ...typography.body, color: colors.gold, fontWeight: '600', marginTop: '2px' }}>{peakWeek.toLocaleString()} kg</div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: spacing.lg,
    backgroundColor: colors.surface1,
    borderRadius: radius.card,
    border: `1px solid ${colors.border}`,
    marginBottom: spacing.lg,
  },
  kpiBox: {
    flex: '1 1 100px',
    padding: spacing.md,
    backgroundColor: colors.surface2,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: spacing.lg,
    paddingTop: spacing.lg,
    borderTop: `1px solid ${colors.border}`,
  },
};

export default TrainingLoad;
